import { useCallback, useEffect, useMemo } from 'react';
import useTenantType from 'utils/hooks/useTenantType';
import { useAppDispatch, useAppSelector } from 'store/configureStore';
import { tenantSettingsTenantModulesSelector } from 'store/selectors/tenant-settings';
import useTenantTranslation from 'utils/hooks/useTenantTranslation';
import {
  profileIsAdvisorSelector,
  profileRoleSelector,
} from 'store/selectors/profile';
import useTenantPageConfiguration from 'utils/hooks/useTenantPageConfiguration';
import { PageConfigurationTypeEnum } from '_types/tenant-settings/tenant-page-configuration';
import { fetchClientsCounts } from 'store/actions/sessions';
import {
  hasPastOrFutureSessionsSelector,
  isSessionsCountsLoadedSelector,
} from 'store/selectors/session';
import { headerTabsConstantsFn } from './utils';

export const useHeaderMenuTabs = ({
  isForUnAuthHomePage,
}: {
  isForUnAuthHomePage: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTenantTranslation();
  const userRole = useAppSelector(profileRoleSelector);
  const tenantType = useTenantType();
  const tenantModules = useAppSelector(tenantSettingsTenantModulesSelector);
  const { pageConfiguration } = useTenantPageConfiguration(
    PageConfigurationTypeEnum.UNAUTH_HOME_PAGE,
  );
  const isSessionsCountsLoaded = useAppSelector(isSessionsCountsLoadedSelector);
  const hasPastOrFutureSessions = useAppSelector(
    hasPastOrFutureSessionsSelector,
  );
  const isAdvisor = useAppSelector(profileIsAdvisorSelector);

  const checkShouldShowClientTab = useCallback(
    () => (isAdvisor ? true : hasPastOrFutureSessions),
    [isAdvisor, hasPastOrFutureSessions],
  );

  const headerTabsConstants = useMemo(() => {
    let headerTabs = headerTabsConstantsFn(
      userRole,
      tenantModules,
      t,
      tenantType,
      pageConfiguration,
    );
    if (isForUnAuthHomePage) {
      headerTabs = headerTabs.filter((header) => header?.isForUnAuthHomePage);
    } else {
      headerTabs = headerTabs.filter((header) => {
        let areConditionsMet = !header?.isForUnAuthHomePage;

        if (header.path === '/clients') {
          areConditionsMet = checkShouldShowClientTab();
        }

        return areConditionsMet;
      });
    }
    return headerTabs;
  }, [
    userRole,
    tenantModules,
    t,
    tenantType,
    isForUnAuthHomePage,
    pageConfiguration,
    checkShouldShowClientTab,
  ]);

  const isAuthedTabsReady = useMemo(
    () => isSessionsCountsLoaded,
    [isSessionsCountsLoaded],
  );

  useEffect(() => {
    if (!isForUnAuthHomePage && !isSessionsCountsLoaded) {
      dispatch(fetchClientsCounts());
    }
  }, [isForUnAuthHomePage, isSessionsCountsLoaded]);

  return {
    tabs: headerTabsConstants,
    tabsReady: isForUnAuthHomePage ? true : isAuthedTabsReady,
  };
};
